import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Transactions from './pages/Transactions'
import Invoices from './pages/Invoices'
import Reconciliation from './pages/Reconciliation'
import Upload from './pages/Upload'
import Reports from './pages/Reports'
import Forecasts from './pages/Forecasts'
import BusinessPlan from './pages/BusinessPlan'
import Chat from './pages/Chat'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import Register from './pages/Register'
import Account from './pages/Account'
import Categorize from './pages/Categorize'
import Integrations from './pages/Integrations'
import Invite from './pages/Invite'
import BusinessIntelligence from './pages/BusinessIntelligence'
import Patrimoniale from './pages/Patrimoniale'
import MaturityDetail from './pages/MaturityDetail'
import Tabellone from './pages/Tabellone'
import SidGenerator from './pages/SidGenerator'
import AdrStorico from './pages/AdrStorico'
import SidFirma from './pages/SidFirma'
import SidAdmin from './pages/SidAdmin'
import Treasury from './pages/Treasury'
import F24 from './pages/F24'
import AIGovernance from './pages/AIGovernance'
import PongoReiPage from './modules/rei/PongoReiPage'
import { authService } from './services/auth'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { UserProvider } from './contexts/UserContext'
import { LanguageProvider } from './contexts/LanguageContext'

const REI_ENABLED = import.meta.env.VITE_REI_ENABLED === 'true'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const location = useLocation()
  const isGoogleCallbackRoute =
    location.pathname === '/auth/google/callback' ||
    location.pathname === '/login/google/callback'

  useEffect(() => {
    // Check if user is logged in
    const token = authService.getToken()
    console.log('[App] Token check:', token ? 'found' : 'not found')
    setIsAuthenticated(!!token)
  }, [])

  const handleLogin = () => {
    // Verify token was actually saved
    const token = authService.getToken()
    console.log('[App] handleLogin called, token:', token ? 'saved' : 'NOT SAVED')
    if (token) {
      setIsAuthenticated(true)
    } else {
      console.error('[App] Token not found after login!')
    }
  }

  // Allow reset-password page without auth
  if (location.pathname === '/reset-password') {
    return <ResetPassword />
  }

  // Allow register page without auth (from Stripe checkout)
  if (location.pathname === '/register') {
    return <Register />
  }

  // Allow invite page without auth
  if (location.pathname.startsWith('/invite/')) {
    return <Invite onLogin={handleLogin} />
  }

  // Handle Google OAuth callback - show Login to process the code
  if (isGoogleCallbackRoute) {
    return <Login onLogin={handleLogin} />
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.clear()
    setIsAuthenticated(false)
    window.location.replace('/')
  }

  return (
    <LanguageProvider>
      <ThemeProvider>
        <UserProvider>
          <WorkspaceProvider>
            <Layout onLogout={handleLogout}>
            <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboards/:dashboardId" element={<Dashboard />} />
            <Route path="/treasury" element={<Treasury />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/reconciliation" element={<Reconciliation />} />
            <Route path="/categorize" element={<Categorize />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/forecasts" element={<Forecasts />} />
            <Route path="/business-plan" element={<BusinessPlan />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/business-intelligence" element={<BusinessIntelligence />} />
            <Route path="/patrimoniale" element={<Patrimoniale />} />
            <Route path="/tabellone" element={<Tabellone />} />
            <Route path="/sid" element={<SidGenerator />} />
            <Route path="/sid/storico" element={<AdrStorico />} />
            <Route path="/sid/certificato" element={<SidFirma />} />
            <Route path="/sid/admin" element={<SidAdmin />} />
            {REI_ENABLED ? <Route path="/rei" element={<PongoReiPage />} /> : null}
            <Route path="/f24" element={<F24 />} />
            <Route path="/maturity" element={<MaturityDetail />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/ai-governance" element={<AIGovernance />} />
            <Route path="/account" element={<Account />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Layout>
          </WorkspaceProvider>
        </UserProvider>
      </ThemeProvider>
    </LanguageProvider>
  )
}

export default App
